import { Link, useParams } from '@tanstack/react-router'
import { ChevronRight, Info } from 'lucide-react'
import { domAnimation, LazyMotion, m } from 'motion/react'

import { useSettings } from '@/hooks/useSettings'
import { useEventCopy } from '@/lib/utils/eventCopy'
import MdiDice5Outline from '~icons/mdi/dice-5-outline'
import MdiGiftOutline from '~icons/mdi/gift-outline'
import MdiHeartOutline from '~icons/mdi/heart-outline'

export const ActivitiesPage = () => {
  const { eventId } = useParams({
    from: '/e/$eventId/_dashboard/activities/',
  })
  const { settings, isLoading } = useSettings()
  const copy = useEventCopy()

  const activitiesDefinitions = [
    {
      to: '/e/$eventId/activities/random-number',
      title: 'กิจกรรมสุ่มเลข (Lucky Draw)',
      description: 'ร่วมลุ้นเป็นผู้โชคดีได้รับรางวัลพิเศษ',
      icon: MdiDice5Outline,
      color: 'bg-secondary/10',
      iconColor: 'text-secondary',
      hoverBg: 'group-hover:bg-secondary/20',
      moduleKey: 'luckyDraw',
    },
    {
      to: '/e/$eventId/activities/wedding-blessing',
      title: `${copy.blessingTitle} (Blessing)`,
      description: `${copy.blessingSubtitle}`,
      icon: MdiHeartOutline,
      color: 'bg-primary/10',
      iconColor: 'text-primary',
      hoverBg: 'group-hover:bg-primary/20',
      moduleKey: 'blessing',
    },
    {
      to: '/e/$eventId/activities/e-angpao',
      title: 'ของขวัญ (E-Angpao)',
      description: copy.angpaoSubtitle,
      icon: MdiGiftOutline,
      color: 'bg-primary/10',
      iconColor: 'text-primary',
      hoverBg: 'group-hover:bg-primary/20',
      moduleKey: 'angpao',
    },
  ] as const

  // Filter activities based on enabled modules settings
  const activeActivities = activitiesDefinitions.filter((act) => {
    const modules = (settings as any)?.modules
    if (!modules) return true
    const key = act.moduleKey as keyof typeof modules
    return modules[key] !== false
  })

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-stone-500">
        กำลังโหลดกิจกรรม...
      </div>
    )
  }

  return (
    <LazyMotion features={domAnimation}>
      <div className="flex flex-1 flex-col overflow-hidden">
        <main className="relative flex-1 space-y-8 overflow-auto px-6 py-10">
          <section className="space-y-2">
            <m.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-xs font-bold tracking-[0.2em] text-stone-400 uppercase"
            >
              ขอให้มีความสุขใน{copy.eventName}
            </m.h2>
            <m.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl font-bold text-stone-900"
            >
              กิจกรรม<span className="text-primary">ทั้งหมด</span>
            </m.h1>
          </section>

          {activeActivities.length === 0 ? (
            <m.section
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-stone-200 bg-stone-50/20 px-4 py-16 text-center"
            >
              <Info className="mb-2 size-8 text-stone-400" />
              <p className="font-bold text-stone-700">
                ยังไม่มีกิจกรรมที่เปิดใช้งานขณะนี้
              </p>
              <p className="mt-1 text-xs text-stone-500">
                กรุณาติดตามกิจกรรมความร่วมสนุกจากพิธีกรในลำดับถัดไป
              </p>
            </m.section>
          ) : (
            <section className="grid gap-4">
              {activeActivities.map((activity, index) => (
                <m.div
                  key={activity.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <Link
                    to={activity.to}
                    params={{ eventId }}
                    className="group relative block overflow-hidden rounded-[2rem] bg-white p-1 shadow-sm transition-all hover:shadow-xl hover:shadow-stone-200/50 active:scale-[0.98]"
                  >
                    <div className="flex items-center gap-5 p-5">
                      <div
                        className={`flex size-16 shrink-0 items-center justify-center rounded-[1.25rem] ${activity.color} ${activity.iconColor} transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 ${activity.hoverBg}`}
                      >
                        <activity.icon className="size-9" />
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <h3 className="group-hover:text-primary text-lg font-bold text-stone-900 transition-colors">
                          {activity.title}
                        </h3>
                        <p className="line-clamp-2 text-sm leading-relaxed text-stone-500">
                          {activity.description}
                        </p>
                      </div>
                      <div className="group-hover:bg-primary/10 group-hover:text-primary flex size-10 items-center justify-center rounded-full bg-stone-50 text-stone-300 transition-all group-hover:translate-x-1">
                        <ChevronRight className="size-5" />
                      </div>
                    </div>

                    {/* Decorative glow on hover */}
                    <div className="to-primary/5 absolute inset-0 bg-linear-to-br from-transparent via-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </m.div>
              ))}
            </section>
          )}

          {/* Future Activities Hint */}
          <m.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-12 flex flex-col items-center gap-4 text-center"
          >
            <div className="h-px w-12 bg-stone-200" />
            <p className="max-w-50 text-xs leading-relaxed font-medium tracking-widest text-stone-400 uppercase">
              เตรียมพบกับความสนุกเพิ่มเติมเร็วๆ นี้
            </p>
          </m.section>
        </main>
      </div>
    </LazyMotion>
  )
}
